import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { CdsButton } from '@cds/react/button';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react'; // Add Auth0 import
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faFolder, faFolderOpen, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { useFolderUpload } from '../components/useFolderUpload';
import FolderUploadForm from '../components/FolderUploadForm';
import "./Search.css";
import "../App.css"; 

const Promotion = () => {
  const [uploads, setUploads] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [expandedSubfolders, setExpandedSubfolders] = useState({});
  const [showSubfolderForm, setShowSubfolderForm] = useState(null);
  const [subfolderFormData, setSubfolderFormData] = useState({ name: '', parentId: null });
  const [showRenameForm, setShowRenameForm] = useState(null);
  const [renameFormData, setRenameFormData] = useState({ name: '', folderId: null });
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [folderPosition, setFolderPosition] = useState(null);
  const [expandedFolderId, setExpandedFolderId] = useState(null);
  const [user_nickname, setUserNickname] = useState('');
  const folderRefs = useRef({});
  const itemsPerPage = 20;
  const navigate = useNavigate();
  const { user, isAuthenticated, getAccessTokenSilently, loginWithRedirect } = useAuth0(); // Add Auth0 hooks
  
  // Use the folder upload hook
  const {
      uploadingToFolder,
      selectedFiles,
      uploadProgress,
      uploadFilesToFolder,
      handleFileSelection,
      handleUploadClick,
      cancelUpload
  } = useFolderUpload();

  const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:10000',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            scope: "read:files read:folders",
          },
        });

        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        };

        const folderResponse = await api.get('/api/folders', { headers });
        const singleFileResponse = await api.get('/api/uploads', { headers });
        
        const folderData = folderResponse.data.folders.map(folder => ({ ...folder, type: 'folder' }));
        const singleFileData = singleFileResponse.data.files.map(file => ({ ...file, type: 'file' }));
        const combinedData = [...folderData, ...singleFileData];
        setUploads(combinedData);
      } catch (error) {
        console.error('Error fetching uploads:', error);
        if (error.error === 'consent_required') {
          await loginWithRedirect({
            authorizationParams: {
              audience: process.env.REACT_APP_AUTH0_AUDIENCE,
              scope: "openid profile email read:files read:folders",
              prompt: "consent"
            },
            appState: {
              returnTo: window.location.pathname
            }
          });
        }
      }
    };

    fetchData();
  }, [getAccessTokenSilently, loginWithRedirect, api]);

  useEffect(() => {
    if (user) {
      const nickname = user.nickname.replace('.', ' ').replace(/\b\w/g, char => char.toUpperCase());
      setUserNickname(nickname);
    }
  }, [user]);

  const handleFileClick = (upload) => {
    if (upload.type === 'folder') {
      handleFolderClick(upload.id);
      return;
    }
    
    const fileExtension = upload.file_url.split('.').pop().toLowerCase();
    const encodedFileKey = encodeURIComponent(upload.file_key);
    
    if (upload.is_promotion || ['jpg', 'png', 'svg'].includes(fileExtension)) {
        navigate(`/view-img/${upload.id}/${encodedFileKey}`);
    } else if (['ppt', 'pptx'].includes(fileExtension)) {
        navigate(`/view-ppt/${upload.id}/${encodedFileKey}`);
    } else if (['doc', 'docx', 'xls', 'xlsx', 'csv', 'txt'].includes(fileExtension)) {
        navigate(`/view-text/${upload.id}/${encodedFileKey}`);
    } else {
        navigate(`/view-pdf/${upload.id}/${encodedFileKey}`);
    }
  };

  const handleFolderClick = (folderId) => {
    const isExpanding = !expandedFolders[folderId];
    
    setExpandedFolders((prevExpandedFolders) => ({
      ...prevExpandedFolders,
      [folderId]: isExpanding,
    }));

    if (isExpanding) {
      // Calculate position for portal
      const folderElement = folderRefs.current[folderId];
      if (folderElement) {
        const rect = folderElement.getBoundingClientRect();
        setFolderPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width
        });
        setExpandedFolderId(folderId);
      }
    } else {
      setFolderPosition(null);
      setExpandedFolderId(null);
    }
  };

  const toggleSubfolder = (folderId) => {
      setExpandedSubfolders(prev => ({
          ...prev,
          [folderId]: !prev[folderId]
      }));
  };

  const showSubfolderFormHandler = (parentId) => {
      setShowSubfolderForm(parentId);
      setSubfolderFormData({ name: '', parentId });
  };

  const hideSubfolderForm = () => {
      setShowSubfolderForm(null);
      setSubfolderFormData({ name: '', parentId: null });
  };

  const showRenameFormHandler = (folderId, currentName) => {
      setShowRenameForm(folderId);
      setRenameFormData({ name: currentName, folderId });
  };

  const hideRenameForm = () => {
      setShowRenameForm(null);
      setRenameFormData({ name: '', folderId: null });
  };

  const renameFolder = async (e) => {
      e.preventDefault();
      if (!renameFormData.name.trim()) {
          alert('Please enter a folder name');
          return;
      }

      try {
          const token = await getAccessTokenSilently({
              authorizationParams: {
                  audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                  scope: 'update:folders',
              },
          });

          await api.put(`/api/folders/${renameFormData.folderId}`, {
              folder_name: renameFormData.name,
          }, {
              headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
              },
          });

          // Refresh data
          await refreshData();
          hideRenameForm();
          alert('Folder renamed successfully');
      } catch (error) {
          console.error('Error renaming folder:', error);
          alert('Error renaming folder. Please try again.');
      }
  };

  const createSubfolder = async (e) => {
      e.preventDefault();
      if (!subfolderFormData.name.trim()) {
          alert('Please enter a subfolder name');
          return;
      }

      try {
          const token = await getAccessTokenSilently({
              authorizationParams: {
                  audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                  scope: 'create:folders',
              },
          });

          await api.post('/api/create-subfolder', {
              parentFolderId: subfolderFormData.parentId,
              folderName: subfolderFormData.name,
              author: user_nickname,
              country: '',
              category: 'Subfolder',
              isPublic: false,
              workpackage: null,
              isMeeting: false,
              isDeliverable: false,
              isContactList: false,
              isPromotion: true,
              isReport: false,
              isPublication: false,
              isTemplate: false,
              allowSharedAccess: false
          }, {
              headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
              },
          });

          // Refresh data
          await refreshData();
          hideSubfolderForm();
          alert('Subfolder created successfully');
      } catch (error) {
          console.error('Error creating subfolder:', error);
          alert('Error creating subfolder. Please try again.');
      }
  };

  // Refresh data function for upload callback
  const refreshData = async () => {
      try {
          const token = await getAccessTokenSilently({
              authorizationParams: {
                  audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                  scope: 'read:files read:folders',
              },
          });
          
          const headers = {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
          };
          
          const folderResponse = await api.get('/api/folders', { headers });
          const singleFileResponse = await api.get('/api/uploads', { headers });
          const folderData = folderResponse.data.folders.map(folder => ({ ...folder, type: 'folder' }));
          const singleFileData = singleFileResponse.data.files.map(file => ({ ...file, type: 'file' }));
          const combinedData = [...folderData, ...singleFileData];
          setUploads(combinedData);
      } catch (error) {
          console.error('Error refreshing data:', error);
          // Reload page as fallback to prevent blank screen
          window.location.reload();
      }
  };

  const handleDownloadClick = async (folder) => {
      const confirmDownload = window.confirm('Do you want to download this folder?');
      if (confirmDownload) {
          try {
              const token = await getAccessTokenSilently({
                  authorizationParams: {
                      audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                      scope: 'read:files create:files delete:files read:folders delete:folders',
                  }
              });
              const headers = {
                  Authorization: `Bearer ${token}`
              };
              
              const response = await api.get(`/api/download-folder/${folder.id}`, {
                  headers,
                  responseType: 'blob',
              });
              const blob = new Blob([response.data], { type: 'application/zip' });
              saveAs(blob, `${folder.folder_name}.zip`);
          } catch (error) {
              console.error('Error downloading folder:', error);
          }
      }
  };

  const handleFileDownload = (file) => {
    if (file.file_url) {
      const link = document.createElement('a');
      link.href = file.file_url;
      link.download = file.file_key || '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Helper function to determine if user can delete an item
  const canUserDelete = (item) => {
      // Always allow deletion if user is the author
      if (user_nickname === item.author) {
          return true;
      }

      return false;
  };

  const handleDeleteClick = async (id, type, author, nickname) => {
      // Remove the old simple author check - permission is controlled by button visibility
      
      const itemType = type === 'folder' ? 'folder (including all subfolders and files)' : 'file';
      const confirmDelete = window.confirm(`Are you sure you want to delete this ${itemType}? This action cannot be undone.`);
      
      if (confirmDelete) {
          try {
              const token = await getAccessTokenSilently({
                  authorizationParams: {
                      audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                      scope: 'read:files create:files delete:files read:folders delete:folders',
                  }
              });
              const headers = { Authorization: `Bearer ${token}` };
              
              const response = await api.delete(`/api/uploads/${id}`, { headers });
              
              // Show success message with details
              if (type === 'folder' && response.data.deletedFolderCount) {
                  alert(`Successfully deleted folder with ${response.data.deletedFolderCount} total folders and ${response.data.files.length} files.`);
              } else {
                  alert('Item deleted successfully.');
              }
              
              // Refresh data (use existing refresh function for each page)
              await refreshData();
              
          } catch (error) {
              console.error('Error deleting item:', error);
              alert('Error deleting item. Please try again.');
          }
      }
  };

  const handlePreviousPage = () => {
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prevPage) => prevPage + 1);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1); // Reset to the first page on search
  };

  const handleStartDateChange = (event) => {
    setStartDate(event.target.value);
    setCurrentPage(1); // Reset to the first page on date change
  };

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value);
    setCurrentPage(1); // Reset to the first page on date change
  };

  const filteredUploads = uploads.filter((upload) => {
    const uploadDate = new Date(upload.upload_date);
    const isWithinDateRange =
      (!startDate || uploadDate >= new Date(startDate)) &&
      (!endDate || uploadDate <= new Date(endDate));
    
    let matchesSearchTerm;
    if (upload.type === 'folder') {
      matchesSearchTerm =
        upload.folder_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        upload.author.toLowerCase().includes(searchTerm.toLowerCase());
    } else {
      matchesSearchTerm =
        upload.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        upload.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
        upload.author.toLowerCase().includes(searchTerm.toLowerCase());
    }
    
    return isWithinDateRange && matchesSearchTerm && upload.is_promotion;
  });

  // Sort the filtered uploads by upload date in descending order
  const sortedUploads = filteredUploads.sort((a, b) => new Date(b.upload_date) - new Date(a.upload_date));

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUploads = sortedUploads.slice(startIndex, endIndex);

  // Recursive function to render nested folders
  const renderNestedFolders = (folders, level = 0, parentPath = "") => {
      return folders?.map((folder, index) => (
          <div key={folder.id} style={{ marginLeft: `${level * 20}px` }}>
              {/* Folder header with name and expand/collapse */}
              <div 
                  onClick={() => toggleSubfolder(folder.id)}
                  style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px',
                      backgroundColor: level > 0 ? 'rgba(255,255,255,0.15)' : 'rgba(209, 221, 162, 0.9)',
                      borderRadius: '8px',
                      margin: '4px 0',
                      cursor: 'pointer',
                      border: '1px solid rgba(74, 144, 164, 0.3)',
                      transition: 'all 0.2s ease',
                      backdropFilter: 'blur(2px)'
                  }}
              >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: level > 0 ? '0.9em' : '1em' }}>
                          📁 {folder.folder_name}
                      </span>
                      {level > 0 && <span style={{ fontSize: '0.8em', color: '#666' }}>(Subfolder)</span>}
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {expandedSubfolders[folder.id] ? '▼' : '▶'}
                      <button 
                          onClick={(e) => {
                              e.stopPropagation();
                              showSubfolderFormHandler(folder.id);
                          }}
                          style={{
                              background: 'rgba(41, 103, 131, 0.8)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              fontSize: '0.8em',
                              cursor: 'pointer'
                          }}
                      >
                          + Subfolder
                      </button>
                      <button 
                          onClick={(e) => {
                              e.stopPropagation();
                              handleUploadClick(folder.id);
                          }}
                          style={{
                              background: 'rgba(76, 175, 80, 0.8)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              fontSize: '0.8em',
                              cursor: 'pointer'
                          }}
                      >
                          📤 Upload Files
                      </button>
                      {user_nickname === folder.author && (
                          <button 
                              onClick={(e) => {
                                  e.stopPropagation();
                                  showRenameFormHandler(folder.id, folder.folder_name);
                              }}
                              style={{
                                  background: 'rgba(255, 152, 0, 0.8)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '4px 8px',
                                  fontSize: '0.8em',
                                  cursor: 'pointer'
                              }}
                          >
                              ✏️ Rename
                          </button>
                      )}
                      {canUserDelete(folder) && (
                          <button 
                              onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(folder.id, 'folder', folder.author, user_nickname);
                              }}
                              style={{
                                  background: 'rgba(244, 67, 54, 0.8)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '4px 8px',
                                  fontSize: '0.8em',
                                  cursor: 'pointer',
                                  marginLeft: '5px'
                              }}
                          >
                              🗑️ Delete
                          </button>
                      )}
                  </div>
              </div>
              
              {/* Show subfolder creation form if active */}
              {showSubfolderForm === folder.id && (
                  <div style={{
                      marginLeft: `${(level + 1) * 20}px`,
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      padding: '12px',
                      borderRadius: '6px',
                      margin: '8px 0'
                  }}>
                      <form onSubmit={createSubfolder} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <input
                              type="text"
                              placeholder="Subfolder name"
                              value={subfolderFormData.name}
                              onChange={(e) => setSubfolderFormData({ ...subfolderFormData, name: e.target.value })}
                              style={{
                                  padding: '6px',
                                  borderRadius: '4px',
                                  border: '1px solid #ccc',
                                  flex: 1
                              }}
                          />
                          <button type="submit" style={{
                              background: '#4CAF50',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '6px 12px',
                              cursor: 'pointer'
                          }}>
                              Create
                          </button>
                          <button type="button" onClick={hideSubfolderForm} style={{
                              background: '#f44336',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '6px 12px',
                              cursor: 'pointer'
                          }}>
                              Cancel
                          </button>
                      </form>
                  </div>
              )}
              
              {/* Show rename form if active */}
              {showRenameForm === folder.id && (
                  <div style={{
                      marginLeft: `${(level + 1) * 20}px`,
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      padding: '12px',
                      borderRadius: '6px',
                      margin: '8px 0'
                  }}>
                      <form onSubmit={renameFolder} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <input
                              type="text"
                              placeholder="New folder name"
                              value={renameFormData.name}
                              onChange={(e) => setRenameFormData({ ...renameFormData, name: e.target.value })}
                              style={{
                                  padding: '6px',
                                  borderRadius: '4px',
                                  border: '1px solid #ccc',
                                  flex: 1
                              }}
                          />
                          <button type="submit" style={{
                              background: '#FF9800',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '6px 12px',
                              cursor: 'pointer'
                          }}>
                              Rename
                          </button>
                          <button type="button" onClick={hideRenameForm} style={{
                              background: '#f44336',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '6px 12px',
                              cursor: 'pointer'
                          }}>
                              Cancel
                          </button>
                      </form>
                  </div>
              )}
              
              {/* Show file upload form if active */}
              {uploadingToFolder === folder.id && (
                  <FolderUploadForm
                      folderId={folder.id}
                      folderName={folder.folder_name}
                      onFileSelection={handleFileSelection}
                      onUpload={(folderId, files) => uploadFilesToFolder(folderId, files, refreshData)}
                      onCancel={cancelUpload}
                      selectedFiles={selectedFiles}
                      uploadProgress={uploadProgress}
                      level={level}
                  />
              )}
              
              {/* Show files in this folder */}
              {folder.files?.map((file, fileIndex) => (
                  <div key={fileIndex} 
                       onClick={() => handleFileDownload(file)}
                       style={{ 
                           marginLeft: `${(level + 1) * 20}px`,
                           backgroundColor: level > 0 ? 'rgba(209, 221, 162, 0.7)' : 'rgba(209, 221, 162, 0.9)',
                           borderRadius: '6px',
                           margin: '4px 0',
                           padding: '10px',
                           cursor: 'pointer',
                           border: '1px solid rgba(74, 144, 164, 0.2)',
                           transition: 'all 0.2s ease',
                           backdropFilter: 'blur(1px)'
                       }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                          <p style={{ 
                              flex: '3', 
                              whiteSpace: 'nowrap', 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis', 
                              margin: 0,
                              fontWeight: '500',
                              minWidth: '200px',
                              fontSize: '0.9em'
                          }}>
                              📄 {(() => {
                                  try {
                                      // Try different filename sources
                                      let filename = file.file || file.file_key?.split('/').pop() || file.file_url?.split('/').pop();
                                      if (!filename) return 'Unknown file';
                                      
                                      // Remove timestamp prefix
                                      filename = filename.replace(/^\d+_/, '');
                                      
                                      // Fix UTF-8 encoding issues
                                      filename = filename
                                          .replace(/%C3%83%C2%85/g, 'Å')
                                          .replace(/%C3%83%C2%84/g, 'Ä')
                                          .replace(/%C3%83%C2%96/g, 'Ö')
                                          .replace(/%C3%83%C2%A4/g, 'ä')
                                          .replace(/%C3%83%C2%A5/g, 'å')
                                          .replace(/%C3%83%C2%B6/g, 'ö')
                                          .replace(/%C3%A5/g, 'å')
                                          .replace(/%C3%A4/g, 'ä')
                                          .replace(/%C3%B6/g, 'ö')
                                          .replace(/Ã¥/g, 'å')
                                          .replace(/Ã¤/g, 'ä')
                                          .replace(/Ã¶/g, 'ö')
                                          .replace(/Ã…/g, 'Å')
                                          .replace(/Ã„/g, 'Ä')
                                          .replace(/Ã–/g, 'Ö');
                                      
                                      return filename.length > 70 ? `${filename.substring(0, 70)}...` : filename;
                                  } catch (e) {
                                      return file.file_key?.split('/').pop() || 'Unknown file';
                                  }
                              })()}
                          </p>
                          <p style={{ 
                              flex: '1.5', 
                              whiteSpace: 'nowrap', 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis', 
                              margin: 0,
                              fontSize: '0.8em',
                              color: '#2582b0'
                          }}>
                              {file.author.length > 20 ? `${file.author.substring(0, 20)}...` : file.author}
                          </p>
                          <p style={{ 
                              flex: '1', 
                              margin: 0,
                              fontSize: '0.8em',
                              color: '#2582b0',
                              textAlign: 'center'
                          }}>
                              {new Date(file.upload_date).toLocaleDateString()}
                          </p>
                          <p style={{ 
                              flex: '0.5', 
                              margin: 0,
                              fontSize: '0.7em',
                              color: '#2582b0',
                              textAlign: 'center',
                              textTransform: 'uppercase',
                              fontWeight: 'bold'
                          }}>
                              {file.file_url ? file.file_url.split('.').pop() : 'N/A'}
                          </p>
                          {canUserDelete(file) && (
                              <FontAwesomeIcon 
                                  className='delete' 
                                  icon={faTrashAlt} 
                                  onClick={(e) => { 
                                      e.stopPropagation(); 
                                      handleDeleteClick(file.id, file.type, file.author, user_nickname); 
                                  }} 
                                  style={{ cursor: 'pointer', color: 'red', marginLeft: '10px' }} 
                              />
                          )}
                      </div>
                  </div>
              ))}
              
              {/* Recursively render subfolders */}
              {expandedSubfolders[folder.id] && folder.subfolders && folder.subfolders.length > 0 && 
                  renderNestedFolders(folder.subfolders, level + 1, `${parentPath}/${folder.folder_name}`)
              }
          </div>
      ));
  };

  return (
    <div className='search-main-div'>
      <h1 className='search-h1'>Search Promotion Materials</h1>
      <div>
        <input className='search-input'
          type="text"
          placeholder="Search by Title, country, or author"
          value={searchTerm}
          onChange={handleSearchChange}
          style={{ marginBottom: '20px', padding: '10px', width: '100%' }}
        />
        <div style={{ marginBottom: '20px' }}>
          <label>
            Start Date:
            <input
              type="date"
              value={startDate}
              onChange={handleStartDateChange}
              style={{ marginLeft: '10px', marginRight: '20px' }}
            />
          </label>
          <label>
            End Date:
            <input
              type="date"
              value={endDate}
              onChange={handleEndDateChange}
              style={{ marginLeft: '10px' }}
            />
          </label>
        </div>
        <div className="list-header" style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            padding: '10px', 
            backgroundColor: '#f0f0f0', 
            fontWeight: 'bold' 
        }}>
            <span style={{ flex: '2.5' }}>Title</span>
            <span style={{ flex: '1.3' }}>Author</span>
            <span style={{ flex: '1.3' }}>Upload Date</span>
            <span style={{ flex: '1' }}>Type of work</span>
            <span style={{ flex: '1.5' }}>Download</span>
            <span style={{ width: '80px', textAlign: 'center' }}>Actions</span>
        </div>
        <ol>
          {paginatedUploads.map((upload, index) => (
            <li 
                className="search-list-item" 
                key={index} 
                ref={upload.type === 'folder' ? (el) => folderRefs.current[upload.id] = el : null}
                onClick={() => handleFileClick(upload)}
            >
              <div className='list-info' style={{ display: 'flex', justifyContent: 'space-between' }}>
                <p style={{ flex: '2.2', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center' }}>
                    {upload.type === 'folder' && (
                        <FontAwesomeIcon 
                            icon={expandedFolders[upload.id] ? faFolderOpen : faFolder} 
                            style={{ marginRight: '8px', color: 'coral' }} 
                        />
                    )}
                    {upload.type === 'folder' ? (upload.folder_name.length > 65 ? `${upload.folder_name.substring(0, 65)}...` : upload.folder_name) : (() => {
                        try {
                            // Try different filename sources
                            let filename = upload.file || upload.file_key?.split('/').pop() || upload.file_url?.split('/').pop();
                            if (!filename) return upload.category || 'Unknown file';
                            
                            // Remove timestamp prefix
                            filename = filename.replace(/^\d+_/, '');
                            
                            // Fix UTF-8 encoding issues
                            filename = filename
                                .replace(/%C3%83%C2%85/g, 'Å')
                                .replace(/%C3%83%C2%84/g, 'Ä')
                                .replace(/%C3%83%C2%96/g, 'Ö')
                                .replace(/%C3%83%C2%A4/g, 'ä')
                                .replace(/%C3%83%C2%A5/g, 'å')
                                .replace(/%C3%83%C2%B6/g, 'ö')
                                .replace(/%C3%A5/g, 'å')
                                .replace(/%C3%A4/g, 'ä')
                                .replace(/%C3%B6/g, 'ö')
                                .replace(/Ã¥/g, 'å')
                                .replace(/Ã¤/g, 'ä')
                                .replace(/Ã¶/g, 'ö')
                                .replace(/Ã…/g, 'Å')
                                .replace(/Ã„/g, 'Ä')
                                .replace(/Ã–/g, 'Ö');
                            
                            return filename.length > 65 ? `${filename.substring(0, 65)}...` : filename;
                        } catch (e) {
                            return upload.file_key?.split('/').pop() || upload.category || 'Unknown file';
                        }
                    })()}
                </p>
                <p style={{ flex: '1.2', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {upload.author.length > 60 ? `${upload.author.substring(0, 60)}...` : upload.author}
                </p>
                <p style={{ flex: '1.5' }}>
                    {new Date(upload.upload_date).toLocaleDateString()}
                </p>
                <p style={{ flex: '2' }}>
                    {upload.is_public && 'Public '}
                    {upload.workpackage && upload.workpackage !== 'null' && `${upload.workpackage} `}
                    {upload.is_meeting && 'Meeting '}
                    {upload.is_deliverable && 'Deliverable '}
                    {upload.is_contact_list && 'Contact List '}
                    {upload.is_promotion && 'Promotion '}
                    {upload.is_report && 'Report '}
                    {upload.is_publication && 'Publication '}
                    {upload.is_template && 'Template'}
                </p>
                <p style={{ 
                    width: '80px',
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    {upload.type === 'folder' && (
                        <>
                            <FontAwesomeIcon 
                                className='folder' 
                                icon={expandedFolders[upload.id] ? faFolderOpen : faFolder} 
                                style={{ cursor: 'pointer', color: 'coral' }} 
                                onClick={() => handleFolderClick(upload.id)} 
                            />
                            <FontAwesomeIcon 
                                className='download' 
                                icon={faDownload} 
                                onClick={(e) => { e.stopPropagation(); handleDownloadClick(upload); }} 
                                style={{ cursor: 'pointer', color: 'blue' }} 
                            />
                        </>
                    )}
                    {canUserDelete(upload) && (
                        <FontAwesomeIcon 
                            className='delete' 
                            icon={faTrashAlt} 
                            onClick={(e) => { 
                                e.stopPropagation(); 
                                handleDeleteClick(upload.id, upload.type, upload.author, user_nickname); 
                            }} 
                            style={{ cursor: 'pointer', color: 'red' }} 
                        />
                    )}
                </p>
              </div>
            </li>
          ))}
        </ol>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '5px' }}>
          <CdsButton onClick={handlePreviousPage} disabled={currentPage === 1}>
            Previous
          </CdsButton>
          <CdsButton onClick={handleNextPage} disabled={endIndex >= sortedUploads.length}>
            Next
          </CdsButton>
        </div>
      </div>
      {folderPosition && expandedFolderId && createPortal(
        <div className="folder-portal-container" style={{
          position: 'absolute',
          top: folderPosition.top,
          left: Math.max(20, Math.min(50, (window.innerWidth - 800) / 2)),
          width: Math.min(800, window.innerWidth - 40),
          minWidth: '600px',
          zIndex: 10000,
          background: 'linear-gradient(135deg, #296783, #1e4d5f)',
          border: '2px solid #4a90a4',
          borderRadius: '15px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
          padding: '20px',
          maxHeight: '500px',
          overflowY: 'auto'
        }}>
          <div style={{ margin: 0, padding: 0 }}>
              {(() => {
                  const folder = uploads.find(item => item.id === expandedFolderId);
                  return renderNestedFolders([folder], 0);
              })()}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Promotion;